import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Globe, ShoppingBag, Sparkles, Tag } from "lucide-react";
import { PRODUCTS } from "../test/catalog";
import { getStoredAppLocale, setStoredAppLocale } from "../../lib/app-locale";

const PAGE_LABELS = {
  ko: {
    badge: "TMTT.Link Store",
    title: "태그 선택하기",
    description: "목적에 맞는 NFC 태그를 선택하고 상세 페이지에서 결제를 진행하세요.",
    backHome: "홈으로",
    viewDetail: "상세보기",
    noticeLabel: "안내",
    notice: "상품을 선택하면 언어에 맞는 상세 페이지와 결제 화면으로 이동합니다.",
  },
  en: {
    badge: "TMTT.Link Store",
    title: "Choose your tag",
    description: "Select the NFC tag that fits your use case and continue to checkout from the detail page.",
    backHome: "Back Home",
    viewDetail: "View Details",
    noticeLabel: "Note",
    notice: "Choose a product to open its localized detail page and checkout flow.",
  },
};

function getProductText(product, field, locale) {
  const value = product[field];
  if (typeof value === "object" && value !== null) {
    return value[locale] ?? value.ko ?? "";
  }
  return value ?? "";
}

function formatPrice(price, locale) {
  const formattedPrice = price.toLocaleString();
  return locale === "en" ? `₩${formattedPrice}` : `${formattedPrice}원`;
}

export default function PaymentPage() {
  const [locale, setLocale] = useState(getStoredAppLocale());
  const labels = PAGE_LABELS[locale];

  const toggleLocale = () => {
    const nextLocale = locale === "ko" ? "en" : "ko";
    setStoredAppLocale(nextLocale);
    setLocale(nextLocale);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.2),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.16),transparent_34%),linear-gradient(180deg,#09090b_0%,#020617_100%)]" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-zinc-950">
              <Tag className="h-5 w-5" />
            </span>
            <span className="text-lg font-semibold tracking-tight">TMTT.Link</span>
          </Link>
          <button
            onClick={toggleLocale}
            title={locale === "ko" ? "Switch to English" : "한국어로 전환"}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Globe className="h-4 w-4" />
            {locale === "ko" ? "EN" : "KO"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" />
          {labels.backHome}
        </Link>

        <section className="mb-8 rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/20 backdrop-blur md:p-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-zinc-300">
            <Sparkles className="h-4 w-4 text-sky-300" />
            {labels.badge}
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <h1 className="text-4xl font-semibold tracking-tight md:text-6xl">{labels.title}</h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-zinc-400 md:text-lg">{labels.description}</p>
            </div>
            <div className="hidden rounded-3xl border border-white/10 bg-white/5 p-5 lg:block">
              <ShoppingBag className="h-8 w-8 text-sky-300" />
            </div>
          </div>
        </section>

        <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(PRODUCTS).map(([key, product]) => {
            const productName = getProductText(product, "name", locale);
            const productDescription = getProductText(product, "description", locale);
            return (
              <Link
                key={key}
                to={`/payment/${key}`}
                className="group overflow-hidden rounded-[1.75rem] border border-white/10 bg-zinc-900/80 shadow-xl shadow-black/20 transition-all hover:-translate-y-1 hover:border-white/20 hover:bg-zinc-900"
              >
                <div className="aspect-[891/1260] overflow-hidden bg-zinc-800">
                  <img
                    src={product.image}
                    alt={productName}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-5">
                  <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-medium text-zinc-300">
                    NFC Tag
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight">{productName}</h2>
                  <p className="mt-2 min-h-10 text-sm leading-6 text-zinc-400">{productDescription}</p>
                  <div className="mt-6 flex items-center justify-between gap-4">
                    <p className="text-lg font-semibold text-white">{formatPrice(product.price, locale)}</p>
                    <span className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-3 text-sm font-semibold text-zinc-950 transition-colors group-hover:bg-sky-100">
                      {labels.viewDetail}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        <section className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-5 text-sm text-zinc-400">
          <span className="font-medium text-zinc-200">{labels.noticeLabel}:</span> {labels.notice}
        </section>
      </main>
    </div>
  );
}
